import { useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Card, Flex, InputRef } from "antd";
import { UserOutlined, CheckCircleOutlined, UserAddOutlined } from "@ant-design/icons";
import MetricCard from "components/MetricCard.component";
import { fetchAnalyticsRows, getClientAnalytics } from "hooks/queries/analytics";
import { translate } from "shared/languages";
import { colors } from "shared/theme/color";
import { getColumnSearchProps } from "shared/components/SearchColumnProps";
import { getYtdDayRange } from "shared/utils/dayjs";
import { AnalyticsRange, ClientAnalytics, ClientAnalyticsRow, CustomDayRange } from "shared/types";
import PATHS from "shared/constants/paths";
import RangeSegmented from "./RangeSegmented";
import CustomRangePicker from "./CustomRangePicker";
import ReportPageHeading from "./ReportPageHeading";
import ExportMenu from "./ExportMenu.component";
import { useAuth } from "context/AuthContextProvider";
import { useTableSort } from "hooks/useTableSort";
import { analyticsRangeStamp } from "shared/utils/download";
import { MAX_PDF_ROWS } from "pdf/layout";
import { loadReportPdf } from "shared/utils/loadPdfChunk";
import { ReportColumn, reportColumn } from "shared/reports/reportColumns";
import { ReportTable } from "shared/reports/ReportTable";
import { clientPerformanceSpec } from "pdf/reports/specs/archetype2";
import { useReportPageCount } from "./useReportPageCount";
import { textPx } from "shared/constants/zoom";

const ClientPerformancePage = () => {
  const navigate = useNavigate();
  const { state: authState } = useAuth();
  const { sortState, onSortChange } = useTableSort();
  // Defaults to YTD — customRange starts pre-populated to match so the first
  // render already queries {from,to} instead of a never-sent range=ytd token.
  const ytdRange = getYtdDayRange();
  const [range, setRange] = useState<AnalyticsRange>("ytd");
  const [customRange, setCustomRange] = useState<CustomDayRange>(ytdRange);
  const isYtdActive = customRange?.from === ytdRange.from && customRange?.to === ytdRange.to;
  const searchInput = useRef<InputRef>(null);
  const { data, isLoading } = getClientAnalytics(range, customRange);

  const columns: ReportColumn<ClientAnalyticsRow>[] = [
    reportColumn<ClientAnalyticsRow>("client", {
      dataIndex: "customer_name",
      search: getColumnSearchProps<ClientAnalyticsRow, "customer_name">({
        config: "customer_name",
        searchInput,
      }),
    }),
    reportColumn<ClientAnalyticsRow>("bookings", {
      dataIndex: "bookings",
      info: () => translate("analyticsPage.client.col_bookings_info"),
      sorter: (a, b) => a.bookings - b.bookings,
      defaultSortOrder: "descend",
    }),
    reportColumn<ClientAnalyticsRow>("no_shows", {
      dataIndex: "no_shows",
      sorter: (a, b) => a.no_shows - b.no_shows,
    }),
    reportColumn<ClientAnalyticsRow>("open_sessions", {
      dataIndex: "open_sessions",
      sorter: (a, b) => a.open_sessions - b.open_sessions,
    }),
    reportColumn<ClientAnalyticsRow>("private_sessions", {
      dataIndex: "private_sessions",
      sorter: (a, b) => a.private_sessions - b.private_sessions,
    }),
    reportColumn<ClientAnalyticsRow>("last_session", { dataIndex: "last_session" }),
  ];

  // One source for both exports: the Excel query string and the PDF's
  // larger-row refetch must describe the same window.
  const windowParams = customRange
    ? { from_date: customRange.from, to_date: customRange.to }
    : { range };

  // The download fetches the full row set when the table holds only the
  // display-capped rows; the picker plans on that same set.
  const pageCount = useReportPageCount(clientPerformanceSpec, data, sortState, {
    needsAll: !!data && data.table_total > data.table.length,
    loadAll: () => fetchAnalyticsRows<ClientAnalytics>("client", { ...windowParams }, MAX_PDF_ROWS),
  });

  return (
    <>
      <ReportPageHeading
        title={translate("analyticsPage.client.report_performance")}
        backTo={PATHS.ANALYTICS}
        extra={
          <ExportMenu
            reportSlug="performa-klien"
            params={windowParams}
            pdf={
              data && {
                rowChoice: {
                  pagesFor: pageCount.pagesFor,
                  prepare: pageCount.prepare,
                  available: Math.min(data.table_total, MAX_PDF_ROWS),
                  total: data.table_total,
                },
                fileName: `spott-${"performa-klien"}-${analyticsRangeStamp(range, customRange)}.pdf`,
                render: async (maxRows) => {
                  // The page holds only the display-capped rows, so a bigger
                  // ask needs its own request; fall back to what we have if
                  // that fetch fails rather than losing the download.
                  const source =
                    (pageCount.source !== data ? pageCount.source : undefined) ??
                    (maxRows && maxRows > data.table.length
                      ? (await fetchAnalyticsRows<ClientAnalytics>(
                          "client",
                          { ...windowParams },
                          maxRows,
                        )) ?? data
                      : data);
                  const { renderReportPdf } = await loadReportPdf();
                  return renderReportPdf({
                    slug: "performa-klien",
                    studio: authState.business?.name ?? "",
                    data: source,
                    sortState,
                    maxRows,
                  });
                },
              }
            }
          />
        }
      />
      <Flex vertical gap={20}>
        <Flex justify="space-between" align="center" wrap="wrap" gap={12}>
          <span style={{ fontSize: textPx(13), color: colors.ink2 }}>
            {data?.range_label}
          </span>
          <Flex align="center" gap={10} wrap="wrap">
            <CustomRangePicker value={isYtdActive ? null : customRange} onChange={setCustomRange} />
            <RangeSegmented
              value={isYtdActive ? "ytd" : range}
              active={!customRange || isYtdActive}
              onChange={(value) => {
                setCustomRange(value === "ytd" ? ytdRange : null);
                setRange(value);
              }}
            />
          </Flex>
        </Flex>
        <Flex gap={16} wrap="wrap">
          <MetricCard
            icon={<UserOutlined />}
            title={translate("analyticsPage.client.kpi_total_clients")}
            value={data?.total_clients.value ?? 0}
            information={translate("analyticsPage.client.kpi_total_clients_info")}
          />
          <MetricCard
            icon={<CheckCircleOutlined />}
            title={translate("analyticsPage.client.kpi_active_clients")}
            value={data?.active_clients.value ?? 0}
            information={translate("analyticsPage.client.kpi_active_clients_info")}
          />
          <MetricCard
            icon={<UserAddOutlined />}
            title={translate("analyticsPage.client.kpi_new_clients")}
            value={data?.new_clients.value ?? 0}
            information={translate("analyticsPage.client.kpi_new_clients_info")}
          />
        </Flex>
        <Card styles={{ body: { padding: 18 } }}>
          <ReportTable<ClientAnalyticsRow>
            serverTotal={{ total: data?.table_total, noun: translate("pdf.noun.klien_dengan_aktivitas") }}
            rowKey="customer_id"
            loading={isLoading}
            columns={columns}
            dataSource={data?.table ?? []}
            pageMemory="analyticsClientPerformance"
            onSortChange={onSortChange}
            onRow={(row) => ({
              onClick: () => navigate(`${PATHS.CUSTOMER}/${row.customer_id}?tab=1`),
              style: { cursor: "pointer" },
            })}
          />
        </Card>
      </Flex>
    </>
  );
};

export default ClientPerformancePage;
